import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarBlank, CaretRight } from 'phosphor-react';
import { useLanguage } from '../context/LanguageContext';

const formatMonthLabel = (language, row) => {
  const formatter = new Intl.DateTimeFormat(language === 'ptBR' ? 'pt-BR' : 'en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return formatter
    .format(new Date(Date.UTC(row.year, row.month - 1, 1)))
    .replace('.', '')
    .toUpperCase();
};

const formatTotalR = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
  const numericValue = Number(value);
  if (numericValue === 0) return '0R';
  return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(2)}R`;
};

const getTotalRColor = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'text-white/55';
  if (Number(value) > 0) return 'text-[#00E676]';
  if (Number(value) < 0) return 'text-[#FF3B30]';
  return 'text-white';
};

const formatProfitFactor = (value, isInfinite) => {
  if (isInfinite) return '∞';
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
  return Number(value).toFixed(2);
};

export default function SharedMonthlyPerformanceSection({ rows = [], buildMonthUrl }) {
  const { t, language } = useLanguage();

  if (!rows.length) return null;

  return (
    <section className="mt-12" data-testid="shared-monthly-performance-section">
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <CalendarBlank size={16} weight="bold" className="text-[#00B8D4]" />
            <h2 className="text-lg font-bold text-white tracking-[0.12em] uppercase" data-testid="shared-monthly-performance-title">
              {t('analytics.monthlyPerformance')}
            </h2>
          </div>
          <p className="text-xs text-white/45 font-mono" data-testid="shared-monthly-performance-hint">
            {t('analytics.monthlyDrilldownHint')}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-sm border border-white/10 bg-[#0D0D0D]" data-testid="shared-monthly-performance-table">
        <div className="hidden grid-cols-[1.3fr_0.75fr_0.85fr_0.85fr_0.9fr_24px] gap-3 border-b border-white/8 px-4 py-3 text-[10px] font-mono uppercase tracking-[0.18em] text-white/38 md:grid" data-testid="shared-monthly-performance-header">
          <div>{t('analytics.monthColumn')}</div>
          <div className="text-right">{t('analytics.operationsColumn')}</div>
          <div className="text-right">{t('analytics.winRate')}</div>
          <div className="text-right">{t('analytics.totalR')}</div>
          <div className="text-right">{t('analytics.profitFactor')}</div>
          <div />
        </div>

        <div data-testid="shared-monthly-performance-rows">
          {rows.map((row) => {
            const monthLabel = formatMonthLabel(language, row);
            const monthUrl = buildMonthUrl(row);

            return (
              <Link
                key={row.month_key}
                to={monthUrl}
                state={{
                  monthlyPerformanceLabel: monthLabel,
                  analyticsScope: 'eligible_closed',
                }}
                className="block border-b border-white/6 last:border-b-0 transition-colors hover:bg-white/[0.03]"
                data-testid={`shared-monthly-performance-row-${row.month_key}`}
              >
                <div className="hidden grid-cols-[1.3fr_0.75fr_0.85fr_0.85fr_0.9fr_24px] items-center gap-3 px-4 py-3 md:grid">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-mono font-semibold text-white" data-testid={`shared-monthly-performance-row-${row.month_key}-month`}>
                      {monthLabel}
                    </span>
                    {row.is_current_month ? (
                      <span className="rounded-sm border border-[#00B8D4]/20 bg-[#00B8D4]/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] text-[#7CE8FF]" data-testid={`shared-monthly-performance-row-${row.month_key}-current`}>
                        {t('analytics.currentMonth')}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-right text-sm font-mono text-white/80" data-testid={`shared-monthly-performance-row-${row.month_key}-trades`}>
                    {row.total_trades}
                  </div>
                  <div className="text-right text-sm font-mono text-white/80" data-testid={`shared-monthly-performance-row-${row.month_key}-win-rate`}>
                    {Number(row.win_rate_excluding_even || 0).toFixed(1)}%
                  </div>
                  <div className={`text-right text-sm font-mono font-bold ${getTotalRColor(row.total_rr)}`} data-testid={`shared-monthly-performance-row-${row.month_key}-total-r`}>
                    {formatTotalR(row.total_rr)}
                  </div>
                  <div className="text-right text-sm font-mono text-white/80" data-testid={`shared-monthly-performance-row-${row.month_key}-profit-factor`}>
                    {formatProfitFactor(row.profit_factor, row.profit_factor_infinite)}
                  </div>
                  <div className="flex justify-end text-white/25">
                    <CaretRight size={14} weight="bold" />
                  </div>
                </div>

                <div className="space-y-3 px-4 py-3 md:hidden">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono font-semibold text-white" data-testid={`shared-monthly-performance-row-${row.month_key}-month-mobile`}>
                          {monthLabel}
                        </span>
                        {row.is_current_month ? (
                          <span className="rounded-sm border border-[#00B8D4]/20 bg-[#00B8D4]/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] text-[#7CE8FF]">
                            {t('analytics.currentMonth')}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <CaretRight size={14} weight="bold" className="text-white/25" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/38">{t('analytics.operationsColumn')}</div>
                      <div className="mt-1 text-sm font-mono text-white/80">{row.total_trades}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/38">{t('analytics.winRate')}</div>
                      <div className="mt-1 text-sm font-mono text-white/80">{Number(row.win_rate_excluding_even || 0).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/38">{t('analytics.totalR')}</div>
                      <div className={`mt-1 text-sm font-mono font-bold ${getTotalRColor(row.total_rr)}`}>{formatTotalR(row.total_rr)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/38">{t('analytics.profitFactor')}</div>
                      <div className="mt-1 text-sm font-mono text-white/80">{formatProfitFactor(row.profit_factor, row.profit_factor_infinite)}</div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}